import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { buildDataTableLabels } from "@/components/table";
import { Link, useSearchParams } from "react-router";
import {
  ArrowUpRight,
  CircleDot,
  CreditCard,
  Lock,
  PencilLine,
  Plug,
  Plus,
  RefreshCw,
  Settings2,
  Trash2,
  Link2Off,
  Unplug,
  Wifi,
  WifiOff,
} from "lucide-react";
import { toast } from "react-hot-toast";
import {
  Badge,
  Button,
  ConfirmModal,
  HoverPopover,
  Spinner,
  Switch,
  Tabs,
  type BadgeVariant,
} from "@/components/ui";
import {
  DataTable,
  DataTablePrimaryText,
  createActionsColumn,
  type DataTableColumn,
} from "@/components/table";
import { PageFrame, ListToolbar, buildListResetToken } from "@/components/common";
import { AdminFilterMenu } from "@/components/admin/AdminFilterMenu";
import { AdminRegionFilter } from "@/components/admin/AdminRegionFilter";
import { RegionCodeBadge } from "@/components/admin/RegionCodeBadge";
import { getApiErrorMessage, toastApiError } from "@/lib/errorMessages";
import { getBatteryIcon } from "@/lib/deviceStatus";
import {
  classifyPairedSumupDeviceStatus,
  getReaderBatteryClass,
  getReaderLiveSnapshot,
  readerLiveStateLabelKey,
  readerLiveStatusBadgeVariant,
  readerPairingStatusLabelKey,
} from "@/lib/readerDeviceStatus";
import {
  formatReaderSerialNumber,
  readerSerialMatchesQuery,
} from "@/lib/readerSerialNumber";
import { matchesTextQuery } from "@/lib/textSearch";
import { deviceKeys, deviceService } from "@/services/deviceService";
import {
  assignedDeviceLabel,
  readerKeys,
  readerService,
} from "@/services/readerService";
import { ClickToCopyId } from "@/components/common/ClickToCopyId";
import { badgeVariantFromConfig } from "@/lib/statusBadgeFromConfig";
import type { ReaderInventory } from "@/types";
import { Permission, ReaderStatus } from "@/types";
import { useAuthStore } from "@/store/authStore";
import { ChangeReaderRegionModal } from "./components/ChangeReaderRegionModal";
import { CreateReaderModal } from "./components/CreateReaderModal";
import { PairReaderModal } from "./components/PairReaderModal";
import { RenameReaderModal } from "./components/RenameReaderModal";
import ReaderPresetsPage from "./presets";
import { cn } from "@/lib/utils";
import {
  canChangeRegion,
  canDelete,
  canPair,
  canUnlinkFromDevice,
  canUnpair,
  countReadersByDeviceStatus,
  countReadersByPairing,
  matchesDeviceStatusFilter,
  matchesPairingFilter,
  type DeviceStatusFilter,
  type PairingFilter,
} from "./readerInventoryRules";

const iconButtonClass =
  "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-surface-500 transition-colors hover:bg-surface-100 hover:text-surface-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50";
/** Inline edit pencil: quiet until its cell is hovered or focused. */
const inlineEditButtonClass = `${iconButtonClass} h-7 w-7 text-surface-400 opacity-0 transition-opacity group-hover/cell:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100`;
/** Linked entity name: quiet until hover, then link-colored. */
const LINKABLE_NAME_CLASS =
  "min-w-0 truncate text-left text-sm text-surface-900 transition-colors duration-75 hover:text-primary-700 hover:underline";
const UNLINK_ICON_CLASS =
  "inline-flex shrink-0 items-center rounded-md p-0.5 leading-none text-surface-400 transition-colors duration-75 hover:bg-error-50 hover:text-error-700 disabled:cursor-not-allowed disabled:opacity-50";

type SortColumn = "brand" | "serial" | "region" | "status" | "device";

const PAIRING_FILTER_OPTIONS: PairingFilter[] = [
  "all",
  "not_paired",
  ReaderStatus.PAIRED,
];

const PAIRING_FILTER_LABEL_KEYS: Record<PairingFilter, string> = {
  all: "page.filterStatusAll",
  not_paired: "page.statusNotPaired",
  [ReaderStatus.PAIRED]: "page.statusPaired",
};

const PAIRING_FILTER_DOT_CLASS: Record<Exclude<PairingFilter, "all">, string> = {
  not_paired: "bg-surface-400",
  [ReaderStatus.PAIRED]: "bg-success-500",
};

const DEVICE_STATUS_FILTER_OPTIONS: DeviceStatusFilter[] = [
  "all",
  "online",
  "unavailable",
  "offline",
  "unknown",
];

const DEVICE_STATUS_FILTER_LABEL_KEYS: Record<DeviceStatusFilter, string> = {
  all: "page.filterStatusAll",
  online: "page.filterDeviceOnline",
  unavailable: "page.filterDeviceUnavailable",
  offline: "page.deviceOffline",
  unknown: "page.filterDeviceUnknown",
};

const DEVICE_STATUS_FILTER_DOT_CLASS: Record<
  Exclude<DeviceStatusFilter, "all">,
  string
> = {
  online: "bg-success-500",
  unavailable: "bg-warning-400",
  offline: "bg-error-500",
  unknown: "bg-surface-400",
};

const READER_STATUS_BADGE = {
  [ReaderStatus.CREATED]: { badgeVariant: "default" },
  [ReaderStatus.NOT_PAIRED]: { badgeVariant: "default" },
  [ReaderStatus.PAIRED]: { badgeVariant: "success" },
} as const satisfies Record<ReaderStatus, { badgeVariant: BadgeVariant }>;

export default function ReadersPage() {
  const { t } = useTranslation("readers");
  const { t: tCommon } = useTranslation("common");
  const tableLabels = buildDataTableLabels(tCommon);
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const focusReaderId = searchParams.get("readerId");
  const didApplyReaderFocusRef = useRef(false);
  const canHardDelete = useAuthStore(
    (s) =>
      s.hasPermission(Permission.PLATFORM_HARD_DELETE) &&
      s.hasPermission(Permission.READERS_UPDATE),
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [pairingFilter, setPairingFilter] = useState<PairingFilter>("all");
  const [deviceStatusFilter, setDeviceStatusFilter] =
    useState<DeviceStatusFilter>("all");
  const [regionFilter, setRegionFilter] = useState<string | null>(null);
  const [showMockReaders, setShowMockReaders] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<{
    kind: "pair" | "region" | "rename" | "delete" | "unpair" | "unlink";
    reader: ReaderInventory;
  } | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [activeTab, setActiveTab] = useState<"readers" | "presets">("readers");
  const [isRefreshingAll, setIsRefreshingAll] = useState(false);

  const busyReaderId =
    actionBusy && pendingAction ? pendingAction.reader.id : null;

  const readerListParams = { region_id: regionFilter ?? undefined };
  const {
    data: readers = [],
    isLoading,
    error: readersQueryError,
    refetch: refetchReaders,
  } = useQuery({
    queryKey: readerKeys.list(readerListParams),
    queryFn: () => readerService.getAll(readerListParams),
    enabled: activeTab === "readers",
  });
  const loadError = readersQueryError
    ? getApiErrorMessage(readersQueryError, "loadReaders")
    : null;

  useEffect(() => {
    didApplyReaderFocusRef.current = false;
  }, [focusReaderId]);

  useEffect(() => {
    if (!focusReaderId || didApplyReaderFocusRef.current || isLoading) {
      return;
    }
    const reader = readers.find((item) => item.id === focusReaderId);
    if (!reader) {
      if (regionFilter !== null) {
        setRegionFilter(null);
      }
      return;
    }
    didApplyReaderFocusRef.current = true;
    setActiveTab("readers");
    setPairingFilter("all");
    setDeviceStatusFilter("all");
    setShowMockReaders(true);
    setSearchQuery(reader.serial_number);
  }, [focusReaderId, isLoading, readers, regionFilter]);

  const invalidateReaderLists = async () => {
    await queryClient.invalidateQueries({ queryKey: readerKeys.lists() });
  };

  const searchMatched = useMemo(() => {
    const query = searchQuery.trim();
    return readers.filter((reader) => {
      if (!showMockReaders && reader.provider.toUpperCase() === "MOCK") {
        return false;
      }
      if (!query) return true;
      return (
        readerSerialMatchesQuery(reader.serial_number, query) ||
        matchesTextQuery(query, [
          reader.brand,
          reader.model,
          reader.provider,
          reader.assigned_device
            ? assignedDeviceLabel(reader.assigned_device)
            : null,
        ], { trim: false })
      );
    });
  }, [readers, searchQuery, showMockReaders]);

  const filteredReaders = useMemo(() => {
    return searchMatched.filter(
      (reader) =>
        matchesPairingFilter(reader, pairingFilter) &&
        matchesDeviceStatusFilter(reader, deviceStatusFilter),
    );
  }, [searchMatched, pairingFilter, deviceStatusFilter]);

  useEffect(() => {
    if (!focusReaderId || !didApplyReaderFocusRef.current) {
      return;
    }
    const frame = window.requestAnimationFrame(() => {
      document
        .getElementById(`reader-row-${focusReaderId}`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [focusReaderId, filteredReaders]);

  const pairingFilterCounts = useMemo(
    () => countReadersByPairing(searchMatched, deviceStatusFilter),
    [searchMatched, deviceStatusFilter],
  );

  const deviceStatusFilterCounts = useMemo(
    () => countReadersByDeviceStatus(searchMatched, pairingFilter),
    [searchMatched, pairingFilter],
  );

  const handleConfirmPendingAction = async () => {
    if (!pendingAction) return;
    const { kind, reader } = pendingAction;
    if (kind !== "delete" && kind !== "unpair" && kind !== "unlink") return;
    const assignedDevice = reader.assigned_device;
    if (kind === "unlink" && assignedDevice == null) return;
    setActionBusy(true);
    try {
      if (kind === "unpair") {
        await readerService.unpairSumup(reader.id, true);
        toast.success(t("page.readerUnpaired"));
      } else if (kind === "unlink") {
        if (assignedDevice == null) return;
        await deviceService.unlinkReader(assignedDevice.id);
        toast.success(t("page.readerUnlinkedFromDevice"));
      } else {
        await readerService.delete(reader.id);
        toast.success(t("page.readerDeleted"));
      }
      setPendingAction(null);
      await invalidateReaderLists();
      if (kind === "unlink") {
        await queryClient.invalidateQueries({ queryKey: deviceKeys.lists() });
      }
    } catch (err) {
      toastApiError(err, "readerOperation");
    } finally {
      setActionBusy(false);
    }
  };

  const handleRefreshAllDeviceStatuses = async () => {
    setIsRefreshingAll(true);
    try {
      const { updated, failed } = await readerService.refreshAllDeviceStatuses();
      if (updated > 0 && failed === 0) {
        toast.success(t("page.deviceStatusesRefreshed"));
      } else if (updated > 0 && failed > 0) {
        toast(t("page.deviceStatusesRefreshedPartial", { updated, failed }));
      } else if (updated === 0 && failed > 0) {
        toast.error(t("page.deviceStatusesRefreshFailed", { failed }));
      } else {
        toast.success(t("page.deviceStatusesNothingToRefresh"));
      }
      await invalidateReaderLists();
    } catch (err) {
      toastApiError(err, "readerOperation");
    } finally {
      setIsRefreshingAll(false);
    }
  };

  const deleteSerialLabel =
    pendingAction?.kind === "delete"
      ? formatReaderSerialNumber(
          pendingAction.reader.serial_number,
          pendingAction.reader.provider,
        )
      : "";

  const deleteConfirmMessage =
    pendingAction?.kind === "delete" &&
    pendingAction.reader.provider.toUpperCase() === "SUMUP" &&
    pendingAction.reader.status === ReaderStatus.PAIRED
      ? t("page.deleteConfirmPaired", { serial: deleteSerialLabel })
      : t("page.deleteConfirm", { serial: deleteSerialLabel });

  const columns = useMemo<DataTableColumn<ReaderInventory, SortColumn>[]>(
    () => [
      {
        id: "brand",
        header: t("page.colBrand"),
        sortKey: "brand",
        sortKind: "text",
        getSortValue: (row) => row.brand,
        defaultWidth: 200,
        cell: (reader) => (
          <span className="flex min-w-0 items-center gap-2.5">
            <span
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-violet-50 text-violet-600 ring-1 ring-inset ring-violet-600/15"
              aria-hidden
            >
              <CreditCard className="h-4 w-4" />
            </span>
            <span className="min-w-0">
              <DataTablePrimaryText className="block truncate">
                {reader.brand}
              </DataTablePrimaryText>
              <span className="mt-0.5 block truncate text-xs text-surface-500">
                {[reader.model, reader.provider].filter(Boolean).join(" · ")}
              </span>
            </span>
          </span>
        ),
      },
      {
        id: "serial",
        header: t("page.colSerial"),
        sortKey: "serial",
        sortKind: "text",
        getSortValue: (row) => row.serial_number,
        defaultWidth: 220,
        cell: (reader) => {
          const isBusy = busyReaderId === reader.id;
          return (
            <div
              id={`reader-row-${reader.id}`}
              className="group/cell flex min-w-0 items-center gap-1"
            >
              <ClickToCopyId
                variant="plain"
                hoverBox={false}
                ellipsis={false}
                className="font-mono text-[13px] tabular-nums"
                value={reader.serial_number}
                displayValue={formatReaderSerialNumber(
                  reader.serial_number,
                  reader.provider,
                )}
              />
              <HoverPopover content={t("renameModal.title")}>
                <button
                  type="button"
                  className={cn(inlineEditButtonClass, isBusy && "opacity-100")}
                  disabled={isBusy}
                  onClick={() => setPendingAction({ kind: "rename", reader })}
                  aria-label={t("renameModal.title")}
                >
                  {isBusy ? (
                    <Spinner size="sm" variant="muted"  loadingLabel={tCommon("loading")} />
                  ) : (
                    <PencilLine className="h-4 w-4" />
                  )}
                </button>
              </HoverPopover>
            </div>
          );
        },
      },
      {
        id: "region",
        header: t("page.colRegion"),
        sortKey: "region",
        sortKind: "text",
        getSortValue: (row) => row.region_code,
        defaultWidth: 120,
        cell: (reader) => {
          const isBusy = busyReaderId === reader.id;
          return (
            <div className="group/cell flex items-center gap-1">
              {reader.region_locked_at ? (
                <HoverPopover content={t("page.regionLockedHint")}>
                  <Badge
                    variant="warning"
                    size="sm"
                    aria-label={`${reader.region_code ?? t("page.regionLocked")} — ${t("page.regionLocked")}`}
                  >
                    {reader.region_code}
                    <Lock aria-hidden />
                  </Badge>
                </HoverPopover>
              ) : (
                <RegionCodeBadge code={reader.region_code} />
              )}
              {canChangeRegion(reader) ? (
                <HoverPopover content={t("page.changeRegionAction")}>
                  <button
                    type="button"
                    className={cn(inlineEditButtonClass, isBusy && "opacity-100")}
                    disabled={isBusy}
                    onClick={() => setPendingAction({ kind: "region", reader })}
                    aria-label={t("page.changeRegionAction")}
                  >
                    {isBusy ? (
                      <Spinner size="sm" variant="muted"  loadingLabel={tCommon("loading")} />
                    ) : (
                      <PencilLine className="h-4 w-4" />
                    )}
                  </button>
                </HoverPopover>
              ) : null}
            </div>
          );
        },
      },
      {
        id: "status",
        header: t("page.colStatus"),
        sortKey: "status",
        sortKind: "text",
        getSortValue: (row) =>
          `${row.status} ${row.device_status_snapshot?.status ?? ""} ${row.device_status_snapshot?.state ?? ""}`,
        defaultWidth: 220,
        cell: (reader) => {
          const snap = getReaderLiveSnapshot(reader);
          const classified = classifyPairedSumupDeviceStatus(snap);
          const liveLabel =
            snap && classified
              ? t(readerLiveStateLabelKey(snap, classified))
              : null;
          const liveVariant = classified
            ? readerLiveStatusBadgeVariant(classified)
            : null;
          const batteryRounded =
            snap?.battery_level != null
              ? Math.round(snap.battery_level)
              : null;
          const BatteryIcon =
            batteryRounded != null ? getBatteryIcon(batteryRounded) : null;
          const batteryClass =
            batteryRounded != null
              ? getReaderBatteryClass(batteryRounded)
              : undefined;
          const checkedAt = snap?.checked_at
            ? new Date(snap.checked_at).toLocaleString()
            : null;
          const hoverContent =
            snap &&
            (checkedAt ||
              snap.last_activity ||
              snap.connection_type ||
              batteryRounded != null) ? (
              <div className="space-y-0.5 text-xs">
                {checkedAt ? (
                  <div>{t("page.deviceLastChecked", { time: checkedAt })}</div>
                ) : null}
                {snap.last_activity ? (
                  <div>
                    {t("page.deviceLastActivity", {
                      time: new Date(snap.last_activity).toLocaleString(),
                    })}
                  </div>
                ) : null}
                {snap.connection_type ? <div>{snap.connection_type}</div> : null}
                {batteryRounded != null ? (
                  <div>{t("page.deviceBattery", { level: batteryRounded })}</div>
                ) : null}
              </div>
            ) : null;

          const content = (
            <div className="inline-flex min-w-0 items-center gap-1.5">
              {liveLabel && liveVariant ? (
                <Badge variant={liveVariant} size="sm">
                  {liveVariant === "error" ? (
                    <WifiOff aria-hidden />
                  ) : (
                    <Wifi aria-hidden />
                  )}
                  {liveLabel}
                </Badge>
              ) : (
                <Badge
                  variant={badgeVariantFromConfig(
                    reader.status,
                    READER_STATUS_BADGE,
                  )}
                  size="sm"
                >
                  {t(readerPairingStatusLabelKey(reader.status))}
                </Badge>
              )}
              {BatteryIcon && batteryRounded != null && batteryClass ? (
                <span
                  className={`inline-flex h-5 items-center gap-1 text-xs font-medium leading-none tabular-nums ${batteryClass}`}
                  aria-label={t("page.deviceBattery", { level: batteryRounded })}
                >
                  <BatteryIcon className="block size-[1em] shrink-0" aria-hidden />
                  <span>{batteryRounded}%</span>
                </span>
              ) : null}
            </div>
          );

          if (!hoverContent) return content;
          return <HoverPopover content={hoverContent}>{content}</HoverPopover>;
        },
      },
      {
        id: "device",
        header: t("page.colDevice"),
        sortKey: "device",
        sortKind: "text",
        getSortValue: (row) =>
          row.assigned_device ? assignedDeviceLabel(row.assigned_device) : "",
        defaultWidth: 180,
        cell: (reader) => {
          if (!reader.assigned_device) {
            return (
              <span className="text-surface-300" aria-hidden>
                —
              </span>
            );
          }
          const deviceLabel = assignedDeviceLabel(reader.assigned_device);
          const openLabel = t("page.openAssignedDevice");
          const isBusy = busyReaderId === reader.id;
          return (
            <div className="flex min-w-0 items-center gap-1.5">
              <HoverPopover
                content={
                  <span className="inline-flex items-center gap-1.5">
                    {openLabel}
                    <ArrowUpRight className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  </span>
                }
              >
                <Link
                  to={`/devices?deviceId=${reader.assigned_device.id}`}
                  className={LINKABLE_NAME_CLASS}
                  aria-label={openLabel}
                >
                  {deviceLabel}
                </Link>
              </HoverPopover>
              {canUnlinkFromDevice(reader) ? (
                <HoverPopover content={t("page.unlinkFromDeviceAction")}>
                  <button
                    type="button"
                    className={UNLINK_ICON_CLASS}
                    disabled={isBusy}
                    onClick={() =>
                      setPendingAction({ kind: "unlink", reader })
                    }
                    aria-label={t("page.unlinkFromDeviceAction")}
                  >
                    <Link2Off className="h-4 w-4" />
                  </button>
                </HoverPopover>
              ) : null}
            </div>
          );
        },
      },
      createActionsColumn(t("page.colActions"), {
        defaultWidth: 120,
        cell: (reader) => {
          const isBusy = busyReaderId === reader.id;
          return (
            <div className="flex items-center justify-center gap-0.5">
              {canPair(reader) ? (
                <HoverPopover content={t("page.pairAction")}>
                  <button
                    type="button"
                    className={iconButtonClass}
                    disabled={isBusy}
                    onClick={() => setPendingAction({ kind: "pair", reader })}
                    aria-label={t("page.pairAction")}
                  >
                    {isBusy ? (
                      <Spinner size="sm" variant="muted" loadingLabel={tCommon("loading")} />
                    ) : (
                      <Plug className="h-4 w-4" />
                    )}
                  </button>
                </HoverPopover>
              ) : null}
              {canUnpair(reader) ? (
                <HoverPopover content={t("page.unpairAction")}>
                  <button
                    type="button"
                    className={`${iconButtonClass} text-error-600 hover:bg-error-50 hover:text-error-700 dark:hover:text-error-800`}
                    disabled={isBusy}
                    onClick={() =>
                      setPendingAction({ kind: "unpair", reader })
                    }
                    aria-label={t("page.unpairAction")}
                  >
                    {isBusy ? (
                      <Spinner size="sm" variant="muted" loadingLabel={tCommon("loading")} />
                    ) : (
                      <Unplug className="h-4 w-4" />
                    )}
                  </button>
                </HoverPopover>
              ) : null}
              {canHardDelete && canDelete(reader) ? (
                <HoverPopover content={t("page.deleteTitle")}>
                  <button
                    type="button"
                    className={`${iconButtonClass} text-error-600 hover:bg-error-50 hover:text-error-700 dark:hover:text-error-800`}
                    disabled={isBusy}
                    onClick={() =>
                      setPendingAction({ kind: "delete", reader })
                    }
                    aria-label={t("page.deleteTitle")}
                  >
                    {isBusy ? (
                      <Spinner size="sm" variant="muted" loadingLabel={tCommon("loading")} />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </button>
                </HoverPopover>
              ) : null}
            </div>
          );
        },
      }),
    ],
    [busyReaderId, canHardDelete, t, tCommon],
  );

  return (
    <PageFrame
      title={t("page.title")}
      description={t("page.subtitle")}
      icon={activeTab === "presets" ? Settings2 : CreditCard}
      actions={
        activeTab === "readers" ? (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              onClick={() => void handleRefreshAllDeviceStatuses()}
              disabled={isRefreshingAll}
              isLoading={isRefreshingAll}
              leftIcon={<RefreshCw className="h-4 w-4" />}
            >
              {t("page.refreshAllDeviceStatuses")}
            </Button>
            <Button
              onClick={() => setIsCreateModalOpen(true)}
              leftIcon={<Plus className="h-4 w-4" />}
            >
              {t("page.newReader")}
            </Button>
          </div>
        ) : undefined
      }
    >

      <Tabs
        variant="segmented"
        ariaLabel={t("page.title")}
        idPrefix="readers"
        activeTab={activeTab}
        onTabChange={setActiveTab}
        tabs={[
          {
            id: "readers",
            label: t("page.tabReaders"),
            icon: <CreditCard className="h-4 w-4" />,
          },
          {
            id: "presets",
            label: t("page.tabPresets"),
            icon: <Settings2 className="h-4 w-4" />,
          },
        ]}
      />

      {activeTab === "presets" ? (
        <ReaderPresetsPage />
      ) : (
        <>
          <ListToolbar
            search={searchQuery}
            onSearchChange={setSearchQuery}
            searchPlaceholder={t("page.searchPlaceholder")}
            searchAccessory={
              <Switch
                checked={showMockReaders}
                onChange={(e) => setShowMockReaders(e.target.checked)}
                label={t("page.showMockReaders")}
              />
            }
            filterChildren={
              <>
                <AdminRegionFilter
                  value={regionFilter}
                  onChange={setRegionFilter}
                  valueKey="id"
                />
                <AdminFilterMenu
                  icon={CircleDot}
                  name={t("page.filterPairing")}
                  value={pairingFilter}
                  defaultValue="all"
                  onChange={setPairingFilter}
                  options={PAIRING_FILTER_OPTIONS.map((option) => ({
                    value: option,
                    label: t(PAIRING_FILTER_LABEL_KEYS[option]),
                    count: pairingFilterCounts[option],
                    dotClassName:
                      option === "all"
                        ? undefined
                        : PAIRING_FILTER_DOT_CLASS[option],
                  }))}
                />
                <AdminFilterMenu
                  icon={Wifi}
                  name={t("page.filterDeviceStatus")}
                  value={deviceStatusFilter}
                  defaultValue="all"
                  onChange={setDeviceStatusFilter}
                  options={DEVICE_STATUS_FILTER_OPTIONS.map((option) => ({
                    value: option,
                    label: t(DEVICE_STATUS_FILTER_LABEL_KEYS[option]),
                    count: deviceStatusFilterCounts[option],
                    dotClassName:
                      option === "all"
                        ? undefined
                        : DEVICE_STATUS_FILTER_DOT_CLASS[option],
                  }))}
                />
              </>
            }
          />

          <DataTable
            labels={tableLabels}
            closeLabel={tCommon("close")}
            loadingLabel={tCommon("loading")}
            rows={filteredReaders}
            columns={columns}
            getRowKey={(row) => row.id}
            defaultSortColumn="brand"
            preferencesKey="readers"
            resetToken={buildListResetToken([
              searchQuery,
              pairingFilter,
              deviceStatusFilter,
              regionFilter ?? "",
              showMockReaders,
            ])}
            loadState={
              isLoading
                ? { status: "loading" }
                : loadError != null
                  ? {
                      status: "error",
                      message: loadError,
                      retryLabel: tCommon("tryAgain"),
                      onRetry: () => void refetchReaders(),
                    }
                  : undefined
            }
            emptyMessage={
              searchQuery.trim() ||
              pairingFilter !== "all" ||
              deviceStatusFilter !== "all"
                ? t("page.emptySearch")
                : t("page.emptyDefault")
            }
            rowClassName="hover:bg-surface-50 dark:hover:bg-surface-100/50"
          />
        </>
      )}

      <CreateReaderModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreated={() => invalidateReaderLists()}
        preferredRegionId={regionFilter}
      />
      <RenameReaderModal
        isOpen={pendingAction?.kind === "rename"}
        reader={pendingAction?.kind === "rename" ? pendingAction.reader : null}
        onClose={() => setPendingAction(null)}
        onRenamed={() => invalidateReaderLists()}
      />
      <PairReaderModal
        isOpen={pendingAction?.kind === "pair"}
        reader={pendingAction?.kind === "pair" ? pendingAction.reader : null}
        onClose={() => setPendingAction(null)}
        onPaired={() => invalidateReaderLists()}
      />
      <ChangeReaderRegionModal
        isOpen={pendingAction?.kind === "region"}
        reader={pendingAction?.kind === "region" ? pendingAction.reader : null}
        onClose={() => setPendingAction(null)}
        onChanged={() => invalidateReaderLists()}
      />
      <ConfirmModal
        closeLabel={tCommon("close")}
        cancelText={tCommon("cancel")}
        isOpen={
          pendingAction?.kind === "delete" ||
          pendingAction?.kind === "unpair" ||
          pendingAction?.kind === "unlink"
        }
        onClose={() => {
          if (!actionBusy) setPendingAction(null);
        }}
        onConfirm={() => void handleConfirmPendingAction()}
        title={
          pendingAction?.kind === "delete"
            ? t("page.deleteTitle")
            : pendingAction?.kind === "unpair"
              ? t("page.unpairAction")
              : t("page.unlinkFromDeviceAction")
        }
        message={
          pendingAction?.kind === "delete"
            ? deleteConfirmMessage
            : pendingAction?.kind === "unpair"
              ? t("page.unpairConfirm", {
                  serial: pendingAction.reader.serial_number,
                })
              : t("page.unlinkFromDeviceConfirm", {
                  serial: pendingAction
                    ? formatReaderSerialNumber(
                        pendingAction.reader.serial_number,
                        pendingAction.reader.provider,
                      )
                    : "",
                  device: pendingAction?.reader.assigned_device
                    ? assignedDeviceLabel(pendingAction.reader.assigned_device)
                    : "",
                })
        }
        confirmText={
          pendingAction?.kind === "delete"
            ? tCommon("delete")
            : pendingAction?.kind === "unpair"
              ? t("page.unpairAction")
              : t("page.unlinkFromDeviceAction")
        }
        isDestructive={pendingAction?.kind !== "unpair"}
        isLoading={actionBusy}
      />
    </PageFrame>
  );
}
